import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState, type FormEvent, type MouseEvent, type ReactNode } from 'react'
import { ApiError } from '../../api/client'
import { meQuery } from '../../api/me'
import {
  companyReportsShellQuery,
  removeSchedule,
  reportSchedulesQuery,
  saveSchedule,
  sendScheduleNow,
  type CompanyReportTab,
  type ReportSchedule,
  type SchedulesPage,
} from '../../api/settingsCompanyReports'
import { Forbidden } from '../../ui/Forbidden'
import { Modal } from '../../ui/Modal'
import { Spinner } from '../../ui/Spinner'
import { LoadFailed } from '../../ui/LoadFailed'
import { usePageStyles } from '../../ui/usePageStyles'

/**
 * Settings -> Reports: capul de familie (`settings/company_reports/index`) si
 * ecranul de programari (`settings/company_reports/scheduled_report`).
 *
 * MACHETELE VECHI, element cu element:
 *  - bara: `views/version-3/settings/company_reports/index.php`, plus
 *    `public/js/views/settings/company_reports/index-1.js`, care deschidea
 *    singur prima fila;
 *  - programarile: `views/version-3/settings/catalog/index.php` (partea de
 *    "Scheduled delivery") si `.../catalog/form.php` (modala).
 *
 * DE CE NU E AICI PANOUL "What requesters see". Macheta programarilor se
 * deseneaza in doua locuri, si ecranul de rapoarte o cerea FARA el
 * (`showVisibility => false`): acolo intrebarea e "cine primeste ce raport si
 * cand", iar ce vede cumparatorul in magazin e raspunsul la altceva si ramane
 * in Settings -> Inventoree -> Service catalog.
 *
 * DE CE NU E O LEGATURA CATRE FIECARE RAPORT. Bara veche avea unsprezece
 * intrari. Portalul de sub zece dintre ele nu mai exista pe server, deci ar fi
 * legaturi moarte - iar aici legaturile moarte se scot, nu se reconstruiesc.
 * Serverul trimite numai filele ecranelor care CHIAR sunt in v4; lista intreaga,
 * cu drepturile si etichetele ei, sta in `App\Settings\Reports\CompanyReports`,
 * si isi primeste ruta pe masura ce fiecare raport se muta.
 *
 * BARA E ORIZONTALA (`od-tabbar`), ca in macheta. Coloana din stanga e a zonei
 * de setari, si o deseneaza `SettingsZone` in jurul paginii.
 */

/** Bara de file plus cutia in care sta fila deschisa. */
export function CompanyReportsFrame({ current, children }: { current: string; children: ReactNode }) {
  const shell = useQuery(companyReportsShellQuery)

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (shell.error) return <LoadFailed what="Company Reports" error={shell.error} onRetry={() => void shell.refetch()} />
  if (!shell.data) return <Spinner />

  return (
    <>
      <nav className="od-tabbar">
        <ul className="od-tabbar-nav">
          <li className="menu-header">Company Reports</li>
          {shell.data.tabs.map((tab) => (
            <ReportTab key={tab.id} tab={tab} current={current} />
          ))}
          <li className="menu-block-divider"></li>
        </ul>
      </nav>
      <div className="page-content-wrapper">{children}</div>
    </>
  )
}

/**
 * Calea din router pentru fiecare fila.
 *
 * ADRESA vine de la server (`tab.route`) si ea ramane adevarul - de-aia
 * `href`-ul se scrie din ea. Tabelul asta e in plus, si numai ca sa se poata
 * face un pas de router in loc de o incarcare de pagina intreaga: rutele lui
 * TanStack sunt tipuri, nu siruri, deci un sir venit de pe server nu se poate
 * da lui `navigate`. O fila pe care n-o gaseste aici merge mai departe ca
 * legatura obisnuita - deci o fila noua nu se strica daca cineva uita randul.
 */
const TAB_ROUTES = {
  easeedesk: '/settings/company_reports/easeedesk_report',
  bi: '/settings/company_reports/bi_report',
  scheduled: '/settings/company_reports/scheduled_report',
  inventoree: '/settings/company_reports/inventoree_report',
  customer: '/settings/company_reports/customer_report',
  cost: '/settings/company_reports/cost_report',
  users_settings: '/settings/company_reports/users_settings_report',
  printeeng: '/settings/company_reports/printeeng_report',
  securitee: '/settings/company_reports/securitee_report',
  productivitee: '/settings/company_reports/productivitee_report',
  proactivitee: '/settings/company_reports/proactivitee_report',
} as const

type KnownTab = keyof typeof TAB_ROUTES

function tabTarget(id: string): (typeof TAB_ROUTES)[KnownTab] | null {
  return id in TAB_ROUTES ? TAB_ROUTES[id as KnownTab] : null
}

function ReportTab({ tab, current }: { tab: CompanyReportTab; current: string }) {
  const navigate = useNavigate()
  const active = tab.id === current
  const target = tabTarget(tab.id)
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (target === null) return
    event.preventDefault()
    void navigate({ to: target })
  }

  return (
    <li className={active ? 'menu-tab active selected' : 'menu-tab'}>
      <a href={'/app/' + tab.route} title={tab.label} onClick={onClick}>
        <i className={tab.icon} aria-hidden="true"></i> {tab.label}
      </a>
    </li>
  )
}

/**
 * `settings/company_reports/index`: bara si atat.
 *
 * `index-1.js` apasa singur prima fila - deci si aici pagina se muta singura pe
 * prima, in loc sa arate o cutie goala.
 */
export function CompanyReportsPage() {
  const shell = useQuery(companyReportsShellQuery)
  const navigate = useNavigate()
  const first = shell.data?.tabs[0]
  const target = first ? tabTarget(first.id) : null
  const href = first ? '/app/' + first.route : ''

  useEffect(() => {
    if (target !== null) {
      void navigate({ to: target, replace: true })

      return
    }
    /* O fila pe care router-ul nu o cunoaste inca: se merge la ea ca la orice
       adresa, nu se ramane pe o cutie goala. */
    if (href !== '') window.location.replace(href)
  }, [target, href, navigate])

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />

  return (
    <CompanyReportsFrame current="">
      {shell.data && shell.data.tabs.length === 0 ? (
        <div id="content">
          <div className="alert alert-info">
            <i className="fas fa-info-circle" aria-hidden="true"></i> There is no report here you may see.
          </div>
        </div>
      ) : (
        <Spinner />
      )}
    </CompanyReportsFrame>
  )
}

/* -------------------------------------------------------------------------
 * Scheduled Reports
 * ---------------------------------------------------------------------- */

export function ScheduledReportsPage() {
  const me = useQuery(meQuery)
  usePageStyles(['css/adm.css'], me.data?.version ?? '')

  return (
    <CompanyReportsFrame current="scheduled">
      <SchedulesPanel />
    </CompanyReportsFrame>
  )
}

/** Ce s-a intamplat, spus cu vorbele SERVERULUI cand le are. */
function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

type Notice = { text: string; kind: 'success' | 'danger' } | null

/*
 * Exportat pentru Settings -> Service Catalog (zona Policies,
 * `settings/catalog/CatalogSettingsPage.tsx`): acolo fragmentul vechi se
 * desena INTREG, cu "What requesters see" deasupra programarilor.
 */
export function SchedulesPanel() {
  const page = useQuery(reportSchedulesQuery)
  const client = useQueryClient()
  const [notice, setNotice] = useState<Notice>(null)
  /** `null` = modala inchisa; altfel randul editat, sau unul gol la "Add". */
  const [editing, setEditing] = useState<ReportSchedule | null>(null)
  const [sending, setSending] = useState(0)

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (page.error) return <LoadFailed what="The scheduled reports" error={page.error} onRetry={() => void page.refetch()} />
  if (!page.data) return <Spinner />

  const data = page.data
  const reload = () => client.invalidateQueries({ queryKey: ['settings', 'company-reports', 'schedules'] })

  const onRemove = (schedule: ReportSchedule) => {
    if (!window.confirm('Stop sending this report?')) return
    removeSchedule(schedule.id)
      .then((answer) => {
        setNotice({ text: answer.message, kind: 'success' })
        void reload()
      })
      .catch((error: unknown) => setNotice({ text: failed(error), kind: 'danger' }))
  }

  const onSend = (schedule: ReportSchedule) => {
    setSending(schedule.id)
    sendScheduleNow(schedule.id)
      .then((answer) => setNotice({ text: answer.message, kind: answer.sent ? 'success' : 'danger' }))
      .catch((error: unknown) => setNotice({ text: failed(error), kind: 'danger' }))
      .finally(() => setSending(0))
  }

  return (
    <div id="content" className="scheduleScreen">
      <header className="panel-heading">
        <h3>
          <strong>Scheduled</strong> delivery
        </h3>
        <label className="color">who is sent which report, and when</label>

        {data.canWrite ? (
          <button
            type="button"
            className="pull-right btn btn-success btn-sm"
            id="scheduleAdd"
            onClick={() => setEditing(blankSchedule(data))}
          >
            <i className="fas fa-plus-circle" aria-hidden="true"></i> Add a schedule
          </button>
        ) : null}

        <div className="catalogNotifications">
          {notice ? (
            <div className={`alert alert-${notice.kind}`}>
              <i
                className={notice.kind === 'success' ? 'fas fa-check' : 'fas fa-exclamation-circle'}
                aria-hidden="true"
              ></i>{' '}
              {notice.text}
            </div>
          ) : null}
        </div>
      </header>

      <p className="text-muted">
        A report somebody has to remember to open is one that gets read in the first week and never again. These arrive
        on their own.
      </p>

      {data.schedules.length === 0 ? (
        <div className="adm-empty-inline">
          <i className="fas fa-paper-plane" aria-hidden="true"></i> Nothing is scheduled. The cost report is only seen
          by whoever opens it.
        </div>
      ) : (
        <table className="table table-hover adm-list">
          <thead>
            <tr>
              <th>Name</th>
              <th>Report</th>
              <th>Customer</th>
              <th>Covers</th>
              <th>When</th>
              <th>To</th>
              <th>Last sent</th>
              <th className="text-right">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {data.schedules.map((schedule) => (
              <tr key={schedule.id} className={schedule.status === 1 ? undefined : 'adm-muted'}>
                <td>
                  <strong>{schedule.name}</strong>
                </td>
                <td>{schedule.reportLabel}</td>
                <td>{schedule.companyName || 'Every customer'}</td>
                <td className="adm-muted">{schedule.spanLabel}</td>
                <td className="adm-nowrap">{schedule.next}</td>
                <td className="adm-muted">{schedule.addresses}</td>
                <td className="adm-muted adm-nowrap">{schedule.lastSentAt || 'never'}</td>
                <td className="text-right adm-nowrap">
                  {data.canWrite ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-info btn-xs scheduleSend"
                        title={
                          data.cronConfigured
                            ? 'Send it now, whatever the clock says'
                            : 'This installation has no cron server, so nothing can be sent on demand'
                        }
                        disabled={sending === schedule.id || !data.cronConfigured}
                        onClick={() => onSend(schedule)}
                      >
                        <i
                          className={sending === schedule.id ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'}
                          aria-hidden="true"
                        ></i>
                      </button>{' '}
                      <button
                        type="button"
                        className="btn btn-default btn-xs scheduleEdit"
                        onClick={() => setEditing(schedule)}
                      >
                        <i className="fas fa-pen" aria-hidden="true"></i>
                      </button>{' '}
                      <button
                        type="button"
                        className="btn btn-danger btn-xs scheduleRemove"
                        onClick={() => onRemove(schedule)}
                      >
                        <i className="fas fa-times" aria-hidden="true"></i>
                      </button>
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing ? (
        <ScheduleForm
          page={data}
          schedule={editing}
          onClose={() => setEditing(null)}
          onSaved={(message) => {
            setEditing(null)
            setNotice({ text: message, kind: 'success' })
            void reload()
          }}
        />
      ) : null}
    </div>
  )
}

/** Un rand gol, cu implicitele din `form.php` (`$value(..., <implicit>)`). */
function blankSchedule(page: SchedulesPage): ReportSchedule {
  return {
    id: 0,
    name: '',
    report: 'service_costs',
    reportLabel: '',
    companyId: 0,
    companyName: '',
    monthsBack: 1,
    spanLabel: '',
    frequency: page.frequencies[0]?.value ?? 1,
    dayOfMonth: 5,
    dayOfWeek: 1,
    hour: 7,
    next: '',
    addresses: '',
    /* Bifata la o programare noua, ca in macheta (`!$isEdit || ...`). */
    attachCsv: true,
    status: 1,
    lastSentAt: '',
  }
}

const HOURS = Array.from({ length: 24 }, (_, hour) => hour)

const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, index) => index + 1)

/** Modala `catalog/form.php`: cine, cand, si care raport. */
function ScheduleForm({
  page,
  schedule,
  onClose,
  onSaved,
}: {
  page: SchedulesPage
  schedule: ReportSchedule
  onClose(): void
  onSaved(message: string): void
}) {
  const isEdit = schedule.id > 0
  const [name, setName] = useState(schedule.name)
  const [report, setReport] = useState(schedule.report)
  const [companyId, setCompanyId] = useState(schedule.companyId)
  const [monthsBack, setMonthsBack] = useState(schedule.monthsBack)
  const [frequency, setFrequency] = useState(schedule.frequency)
  const [dayOfMonth, setDayOfMonth] = useState(schedule.dayOfMonth)
  const [dayOfWeek, setDayOfWeek] = useState(schedule.dayOfWeek)
  const [hour, setHour] = useState(schedule.hour)
  const [addresses, setAddresses] = useState(schedule.addresses)
  const [attachCsv, setAttachCsv] = useState(schedule.attachCsv)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  /* Saptamanal si lunar cer campuri diferite, deci se vede numai cel care
     se potriveste - `toggleFrequency` din macheta. */
  const weekly = frequency === 2

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError('')
    saveSchedule({
      ID: schedule.id,
      name,
      report,
      company_id: companyId,
      months_back: monthsBack,
      frequency,
      day_of_month: dayOfMonth,
      day_of_week: dayOfWeek,
      hour,
      addresses,
      attach_csv: attachCsv ? 1 : 0,
      /* `<input type="hidden" name="status" value="1">` din macheta: o
         programare salvata e o programare pornita. */
      status: 1,
    })
      .then((answer) => onSaved(answer.message))
      .catch((problem: unknown) => setError(failed(problem)))
      .finally(() => setBusy(false))
  }

  return (
    <Modal
      id="catalogModal"
      open
      onClose={onClose}
      large
      title={
        <>
          <i className="fas fa-paper-plane" aria-hidden="true"></i> {isEdit ? schedule.name : 'Add a schedule'}
        </>
      }
    >
      <form id="catalogForm" onSubmit={onSubmit}>
        {error ? (
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {error}
          </div>
        ) : null}

        <div className="row">
          <div className="col-md-4 form-group">
            <label htmlFor="schName">Name</label>
            <input
              type="text"
              className="form-control"
              id="schName"
              maxLength={120}
              placeholder="Monthly cost report"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="col-md-4 form-group">
            <label htmlFor="schReport">Report</label>
            <select
              className="form-control"
              id="schReport"
              value={report}
              onChange={(event) => setReport(event.target.value)}
            >
              {page.reports.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-4 form-group">
            <label htmlFor="schCompany">Customer</label>
            {/* Gol inseamna toti clientii - ce vrea o echipa financiara proprie,
                si ce nu are voie sa aiba niciodata un om al clientului. */}
            <select
              className="form-control"
              id="schCompany"
              value={companyId === 0 ? '' : String(companyId)}
              onChange={(event) => setCompanyId(event.target.value === '' ? 0 : Number(event.target.value))}
            >
              <option value="">Every customer</option>
              {page.companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row">
          <div className="col-md-4 form-group">
            <label htmlFor="schSpan">Covers</label>
            <select
              className="form-control"
              id="schSpan"
              value={monthsBack}
              onChange={(event) => setMonthsBack(Number(event.target.value))}
            >
              {page.spans.map((span) => (
                <option key={span.value} value={span.value}>
                  {span.label}
                </option>
              ))}
            </select>
            <span className="text-muted small">
              Always ending with the last complete month &mdash; a figure for a month still running reads as a whole
              one.
            </span>
          </div>

          <div className="col-md-3 form-group">
            <label htmlFor="schFrequency">Sent</label>
            <select
              className="form-control"
              id="schFrequency"
              value={frequency}
              onChange={(event) => setFrequency(Number(event.target.value))}
            >
              {page.frequencies.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3 form-group" id="schMonthlyDay" hidden={weekly}>
            <label htmlFor="schDayOfMonth">On day</label>
            <select
              className="form-control"
              id="schDayOfMonth"
              value={dayOfMonth}
              onChange={(event) => setDayOfMonth(Number(event.target.value))}
            >
              {DAYS_OF_MONTH.map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
            <span className="text-muted small">Up to 28, so it never skips February.</span>
          </div>

          <div className="col-md-3 form-group" id="schWeeklyDay" hidden={!weekly}>
            <label htmlFor="schDayOfWeek">On</label>
            <select
              className="form-control"
              id="schDayOfWeek"
              value={dayOfWeek}
              onChange={(event) => setDayOfWeek(Number(event.target.value))}
            >
              {page.weekdays.map((day) => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-2 form-group">
            <label htmlFor="schHour">At</label>
            <select
              className="form-control"
              id="schHour"
              value={hour}
              onChange={(event) => setHour(Number(event.target.value))}
            >
              {HOURS.map((one) => (
                <option key={one} value={one}>
                  {String(one).padStart(2, '0')}:00
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="schAddresses">To</label>
          <textarea
            className="form-control"
            id="schAddresses"
            rows={2}
            value={addresses}
            onChange={(event) => setAddresses(event.target.value)}
          />
          <span className="text-muted small">
            Separated by commas or new lines. An address that is not one is named back rather than dropped &mdash;
            quietly discarding one is how somebody stops receiving a report without anybody realising.
          </span>
        </div>

        <label className="adm-policy">
          <input type="checkbox" checked={attachCsv} onChange={(event) => setAttachCsv(event.target.checked)} />
          <strong>Attach the figures as a file.</strong> A spreadsheet, so the numbers can be worked with rather than
          retyped.
        </label>

        {/* `Modal` pune copiii in `.modal-body`, deci subsolul se trage inapoi
            la marginile ferestrei, ca in machete. Acelasi truc ca la
            operatiile de la distanta ale aparatului. */}
        <div className="modal-footer" style={{ margin: '15px -15px -15px', padding: '15px' }}>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            <i className={busy ? 'fas fa-spinner fa-spin' : 'fas fa-check'} aria-hidden="true"></i> Save
          </button>
        </div>
      </form>
    </Modal>
  )
}
